/* Whether a holding is a coin or a company, and everything that turns on it.
 *
 * The book was built around listed equities, and almost all of it transfers to
 * a coin unchanged: it has a weight, a ceiling, a thesis, a cost basis and a
 * realised result, and every one of those means the same thing. Four things do
 * not transfer, and they are the reason this file exists rather than a boolean
 * scattered through the screens.
 *
 * **A coin never closes.** The book's session is the most awake state across
 * what it holds, and one instrument that is always awake would report the book
 * as OPEN at three in the morning — which is not a cosmetic problem. The
 * session is what tells the app to price a shut listing from MEXC's index
 * (`state/offHours.ts`), and a book permanently OPEN would silently switch that
 * off. So the session is read from the listed holdings only, and the coins are
 * left out of it.
 *
 * **A coin has no accounts.** No earnings date, no dividend, no quarterly
 * revenue, no Rule of 40. The screens that ask for those get nothing back and
 * would print empty frames — worse than absent, because an empty frame reads as
 * a fetch that failed.
 *
 * **A coin is not counted in shares.** Nobody holds 0.35 shares of Bitcoin.
 *
 * **Yahoo does not list every coin.** It answers for a few hundred; MEXC's
 * board carries over a thousand. So a coin Yahoo will not quote falls back to
 * the contract's index price, the same reading already trusted for a company
 * whose exchange is shut — and the screen says where the price came from,
 * because a figure from a second source is not the same fact as one from the
 * first. */

import type { Holding, Quote, Watch } from '../types';

export type AssetKind = 'equity' | 'crypto';

/** Yahoo's exchange code for its crypto feed. Every coin it quotes comes back
    with this, and nothing else does. */
const CRYPTO_EXCHANGE = 'CCC';

/** What the search calls a coin, so the type survives from the hit that was
    picked to the holding that is written. */
export function kindFromSearchType(type: string | undefined): AssetKind {
  return (type ?? '').toUpperCase() === 'CRYPTOCURRENCY' ? 'crypto' : 'equity';
}

/* Stored on the holding when it is added, and read off the quote when it was
 * not — a book written before coins existed has no field to read, and it should
 * still behave. The stored answer wins: §3.2's rule that the fetch symbol is
 * recorded rather than worked out at call time applies here for the same
 * reason, that a quote may not have arrived yet and a cold start should not
 * change what something is. */
export function kindOf(
  record: Pick<Holding | Watch, 'assetKind'> | undefined,
  quote?: Quote | undefined,
): AssetKind {
  if (record?.assetKind) return record.assetKind;
  return quote && quote.exchangeName === CRYPTO_EXCHANGE ? 'crypto' : 'equity';
}

export function isCrypto(
  record: Pick<Holding | Watch, 'assetKind'> | undefined,
  quote?: Quote | undefined,
): boolean {
  return kindOf(record, quote) === 'crypto';
}

/** True for a quote that is one of Yahoo's coins, whatever the book says. Used
    where only the quote is to hand — working out the session, for one. */
export function quoteIsCrypto(q: Quote): boolean {
  return q.exchangeName === CRYPTO_EXCHANGE;
}

/** MEXC's perpetual on a coin. `BTC` on the exchange screen is `BTC_USDT` in
    the API — no `STOCK` in it, which is the whole difference from
    `stockPerpSymbol` and the reason both exist rather than one with a flag. */
export function coinPerpSymbol(ticker: string): string {
  return `${ticker.trim().toUpperCase()}_USDT`;
}

/** The bare coin out of a Yahoo symbol: `BTC-USD` is BTC. Anything without the
    suffix is handed back as it came, so a symbol typed by hand still works. */
export function coinOf(yahooSymbol: string): string {
  const s = yahooSymbol.trim().toUpperCase();
  const dash = s.lastIndexOf('-');
  return dash > 0 ? s.slice(0, dash) : s;
}

/** What a quantity of this is called. "Shares" against 0.35 BTC is wrong in the
    way that makes somebody check whether the app understood what they typed. */
export function unitWord(kind: AssetKind, plural = true): string {
  if (kind === 'crypto') return plural ? 'units' : 'unit';
  return plural ? 'shares' : 'share';
}
